import React, {
    memo,
    useState,
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useMemo,
} from 'react';
import { createUseStyles, Input } from '@Sage/hoi-poi-ui';
import { useWeb5Context } from 'lib/Web5Context';
import {
    applyLineSmartFieldOverridesToInputParams,
    getLineSmartFieldOverrides,
    toCellValidationError,
} from './applyLineSmartFieldOverrides';
import styles from './styles';

const useStyles = createUseStyles(styles, 'InputCell');

const decimalFilter = (text) => {
    let textArr = [];
    let textValue = text.replace(/[^-,.0-9\s]/g, '');
    textValue.split('').forEach((current, index) => {
        if (index === 0 && current !== '.' && current !== ',') {
            textArr.push(current);
        } else if (
            index > 0 &&
            (current === '.' || current === ',') &&
            !textArr.includes('.') &&
            !textArr.includes(',') &&
            !isNaN(parseInt(textArr[index - 1], 10))
        ) {
            textArr.push(current);
        } else if (current !== '-' && current !== '.' && current !== ',') {
            textArr.push(current);
        }
    });
    textValue = textArr.join('');
    textValue = textValue.replace(',', '.');
    return textValue;
};

const editableDecimalFilter = (text) => {
    let textArr = [];
    let textValue = String(text ?? '').replace(/[^-,.0-9\s]/g, '');
    textValue.split('').forEach((current, index) => {
        if (index === 0 && current !== '.' && current !== ',') {
            textArr.push(current);
        } else if (
            index > 0 &&
            (current === '.' || current === ',') &&
            !textArr.includes('.') &&
            !textArr.includes(',') &&
            !isNaN(parseInt(textArr[index - 1], 10))
        ) {
            textArr.push(current);
        } else if (current !== '-' && current !== '.' && current !== ',') {
            textArr.push(current);
        }
    });

    return textArr.join('');
};

const filters = {
    natural: (text) => {
        return ('' + text).replace(/[^0-9\s]/g, '');
    },
    integer: (text) => {
        return ('' + text).replace(/[^-0-9\s]/g, '');
    },
    number: (text) => {
        return ('' + text).replace(/[^-0-9\s]/g, '');
    },
    decimal: editableDecimalFilter,
    currency: editableDecimalFilter,
    percentage: editableDecimalFilter,
};

const blurInteger = (text) => {
    return parseInt(text, 10) || 0;
};

const blurDecimal = (text) => {
    return parseFloat(decimalFilter(String(text ?? ''))) || 0.0;
};

const blurPercentage = (text) => {
    return parseFloat(decimalFilter(String(text ?? ''))) || 0.0;
};

const blurFilters = {
    natural: blurInteger,
    integer: blurInteger,
    number: blurInteger,
    decimal: blurDecimal,
    currency: blurDecimal,
    percentage: blurPercentage,
};

const NAVIGATION_KEYS = new Set([
    'Backspace',
    'Delete',
    'ArrowLeft',
    'ArrowRight',
    'ArrowUp',
    'ArrowDown',
    'Home',
    'End',
    'Tab',
    'Enter',
    'Escape',
]);

const getNormalizedDecimalParts = (value) => {
    if (value === null || value === undefined || value === '') return null;

    const rawValue = String(value).replace(/[^-,.0-9\s]/g, '');
    const normalizedValue = decimalFilter(rawValue);
    if (!normalizedValue) return null;

    const numericValue = Number(normalizedValue);
    if (!Number.isFinite(numericValue)) {
        if (normalizedValue !== '-') return null;
    }

    const separatorMatch = rawValue.match(/[.,]/);
    const decimalSeparator = separatorMatch?.[0] || '.';
    const hasDecimalSeparator = rawValue.includes('.') || rawValue.includes(',');
    const [integerPart, decimalPart = ''] = normalizedValue.split('.');

    return {
        rawValue,
        normalizedValue,
        decimalSeparator,
        hasDecimalSeparator,
        integerPart,
        decimalPart,
    };
};

const clampValueByDecimals = (value, limitDecimals) => {
    const normalizedParts = getNormalizedDecimalParts(value);
    if (!normalizedParts) return '';

    const { rawValue, decimalSeparator, hasDecimalSeparator, integerPart, decimalPart } =
        normalizedParts;

    if (limitDecimals === undefined || limitDecimals === null) {
        return rawValue;
    }

    if (limitDecimals <= 0) {
        return integerPart;
    }

    if (!hasDecimalSeparator) {
        return integerPart;
    }

    const clampedDecimal = decimalPart.slice(0, limitDecimals);
    return clampedDecimal
        ? `${integerPart}${decimalSeparator}${clampedDecimal}`
        : `${integerPart}${decimalSeparator}`;
};

const insertAtSelection = (currentValue, insertedValue, selectionStart, selectionEnd) => {
    return currentValue.slice(0, selectionStart) + insertedValue + currentValue.slice(selectionEnd);
};

const formatEditableValue = (rawValue, type, limitDecimals) => {
    if (rawValue === null || rawValue === undefined || rawValue === '') return '';

    if (
        (type !== 'decimal' && type !== 'currency' && type !== 'percentage') ||
        limitDecimals === undefined ||
        limitDecimals === null
    ) {
        return rawValue;
    }

    const normalizedParts = getNormalizedDecimalParts(rawValue);
    if (!normalizedParts) return rawValue;

    return Number.parseFloat(normalizedParts.normalizedValue).toFixed(limitDecimals);
};

const getPlaceholderByDecimals = (decimals, fallbackDecimals) => {
    const parsedDecimals = Number.parseInt(decimals, 10);
    const hasValidDecimals = Number.isInteger(parsedDecimals) && parsedDecimals >= 0;
    const finalDecimals = hasValidDecimals ? parsedDecimals : fallbackDecimals;

    if (finalDecimals <= 0) return '0';
    return `0.${'0'.repeat(finalDecimals)}`;
};

const InputCell = memo((params) => {
    const { getDecimalsByConfig, DECIMAL_TYPES } = useWeb5Context();
    const inputRef = useRef();
    const [error, setError] = useState('');
    const classes = useStyles();
    const lastCommittedValueRef = useRef();

    const field = params.colDef.field;
    const lineOverrides = getLineSmartFieldOverrides(params.data, field);
    const resolvedInputParams =
        applyLineSmartFieldOverridesToInputParams(params.inputParams, lineOverrides) ?? {};
    const hasInputParams = Boolean(params.inputParams);

    const {
        onChange,
        type,
        idCurrency,
        idEnvironment,
        limitDecimals: inputLimitDecimals,
        ...inputParams
    } = resolvedInputParams;

    const { defaultValue, isMandatory } = inputParams;

    // Calculate limitDecimals from configuration if idCurrency or idEnvironment are provided
    const limitDecimals = useMemo(() => {
        if (inputLimitDecimals !== undefined && inputLimitDecimals !== null) {
            return inputLimitDecimals;
        }

        if (
            (type === 'decimal' || type === 'currency' || type === 'percentage') &&
            (idCurrency || idEnvironment)
        ) {
            let decimalType;
            if (type === 'currency') {
                decimalType = DECIMAL_TYPES.CURRENCY_PRICES;
            } else if (type === 'percentage') {
                decimalType = DECIMAL_TYPES.GENERAL;
            } else {
                decimalType = DECIMAL_TYPES.GENERAL;
            }

            if (field === 'quantity') {
                decimalType = DECIMAL_TYPES.QUANTITY;
            }

            return getDecimalsByConfig({
                type: decimalType,
                idEnvironment,
                idCurrency,
                entityName: 'SALESORDERSLINES',
                fieldId: field,
            });
        }

        return undefined;
    }, [
        inputLimitDecimals,
        type,
        idCurrency,
        idEnvironment,
        field,
        getDecimalsByConfig,
        DECIMAL_TYPES.CURRENCY_PRICES,
        DECIMAL_TYPES.GENERAL,
        DECIMAL_TYPES.QUANTITY,
    ]);

    const getFormattedValue = useCallback(
        (rawValue) => formatEditableValue(rawValue, type, limitDecimals),
        [limitDecimals, type],
    );

    const [value, setValue] = useState(() => getFormattedValue(params.value ?? ''));
    const valueRef = useRef(getFormattedValue(params.value ?? ''));

    const commitValue = useCallback(
        (nextValue) => {
            const id = params.data.id || params.data.Id || params.data.ID || '';
            lastCommittedValueRef.current = nextValue;
            onChange({ rowIndex: params.node.rowIndex, id, field, value: nextValue });
        },
        [field, onChange, params],
    );

    // Prefer row data over params.value — after clamping, the store holds the correct
    // value but AG Grid's params.value may still reflect the stale user input.
    const rowData = params.data;
    const dataValue = rowData?.[field] ?? rowData?.[field?.toLowerCase?.()] ?? params.value;

    useLayoutEffect(() => {
        const formattedValue = getFormattedValue(dataValue ?? '');

        if (formattedValue !== valueRef.current) {
            setError('');
            valueRef.current = formattedValue;
            setValue(formattedValue);
        }
        // rowData reference changes on every recalculation even when the scalar
        // dataValue is identical (e.g. discount clamped back to the same amount).
        // Including it guarantees the effect re-runs and reconciles local state.
    }, [getFormattedValue, dataValue, rowData]);

    useEffect(() => {
        const rowIndex = params.node.rowIndex;
        const rowError = inputParams?.errors?.rows?.[rowIndex]?.error;
        setError(toCellValidationError(rowError));
    }, [inputParams.errors, params]);

    const handleOnChange = useCallback(
        (val) => {
            const filter = filters[type] || null;
            const previousValue = valueRef.current;
            let newValue = val ?? '';
            if (filter) {
                newValue = filter(newValue);
            }

            if (
                (type === 'decimal' || type === 'currency' || type === 'percentage') &&
                limitDecimals !== undefined &&
                limitDecimals !== null
            ) {
                newValue = clampValueByDecimals(newValue, limitDecimals);
            }

            setError('');

            // When user clears the input:
            // - if it's mandatory and has a defaultValue, immediately restore that defaultValue
            // - otherwise, immediately commit the cleared value (instead of waiting for blur)
            if (newValue === '' && previousValue !== '') {
                if (
                    isMandatory &&
                    defaultValue !== undefined &&
                    defaultValue !== null &&
                    defaultValue !== ''
                ) {
                    let restoredValue = defaultValue;
                    if (filter) restoredValue = filter(restoredValue);

                    valueRef.current = restoredValue;
                    setValue(restoredValue);

                    const blurFilter = blurFilters[type] || null;
                    const committedValue = blurFilter ? blurFilter(restoredValue) : restoredValue;
                    if (committedValue !== lastCommittedValueRef.current) {
                        commitValue(committedValue);
                    }
                    return;
                }

                valueRef.current = newValue;
                setValue(newValue);

                const blurFilter = blurFilters[type] || null;
                const committedValue = blurFilter ? blurFilter(newValue) : newValue;
                if (committedValue !== lastCommittedValueRef.current) {
                    commitValue(committedValue);
                }
                return;
            }

            valueRef.current = newValue;
            setValue(newValue);
        },
        [commitValue, defaultValue, isMandatory, limitDecimals, type],
    );

    const handleOnKeyDown = useCallback(
        (event) => {
            if (
                (type !== 'decimal' && type !== 'currency' && type !== 'percentage') ||
                limitDecimals === undefined ||
                limitDecimals === null
            )
                return;

            if (event.ctrlKey || event.metaKey || event.altKey) return;
            if (NAVIGATION_KEYS.has(event.key)) return;
            if (!/^[0-9.,-]$/.test(event.key)) {
                event.preventDefault();
                return;
            }

            const currentValue = valueRef.current || '';
            const target = event.target;
            const selectionStart = target?.selectionStart ?? currentValue.length;
            const selectionEnd = target?.selectionEnd ?? currentValue.length;
            const nextValue = insertAtSelection(
                currentValue,
                event.key,
                selectionStart,
                selectionEnd,
            );
            const clampedValue = clampValueByDecimals(nextValue, limitDecimals);

            if (nextValue !== clampedValue) {
                event.preventDefault();
            }
        },
        [limitDecimals, type],
    );

    const handleOnPaste = useCallback(
        (event) => {
            if (
                (type !== 'decimal' && type !== 'currency' && type !== 'percentage') ||
                limitDecimals === undefined ||
                limitDecimals === null
            )
                return;

            const pastedText = event.clipboardData?.getData('text') || '';
            if (!pastedText) return;

            const currentValue = valueRef.current || '';
            const target = event.target;
            const selectionStart = target?.selectionStart ?? currentValue.length;
            const selectionEnd = target?.selectionEnd ?? currentValue.length;
            const nextValue = insertAtSelection(
                currentValue,
                pastedText,
                selectionStart,
                selectionEnd,
            );
            const clampedValue = clampValueByDecimals(nextValue, limitDecimals);

            if (nextValue !== clampedValue) {
                event.preventDefault();
                valueRef.current = clampedValue;
                setValue(clampedValue);
                setError('');
            }
        },
        [limitDecimals, type],
    );

    const handleOnBlur = useCallback(() => {
        const blurFilter = blurFilters[type] || null;
        let newValue = valueRef.current;
        if (blurFilter) {
            newValue = blurFilter(valueRef.current);
        }

        if (
            (type === 'decimal' || type === 'currency' || type === 'percentage') &&
            limitDecimals !== undefined &&
            limitDecimals !== null
        ) {
            if (newValue !== undefined && newValue !== null && !isNaN(newValue)) {
                newValue = newValue.toFixed(limitDecimals);
            }
        }

        const displayValue = getFormattedValue(newValue);
        valueRef.current = displayValue;
        setValue(displayValue);

        if (newValue === lastCommittedValueRef.current) return;
        commitValue(newValue);
    }, [commitValue, getFormattedValue, type, limitDecimals]);

    const handleOnEnter = useCallback(() => {
        handleOnBlur();
        inputRef.current?.blur();
    }, [handleOnBlur]);

    const overrides = useMemo(
        () => ({
            input: { ref: inputRef },
        }),
        [],
    );

    const placeholder = useMemo(() => {
        if (type === 'currency') {
            return getPlaceholderByDecimals(limitDecimals, 2);
        }

        if (type === 'decimal' || type === 'percentage') {
            return getPlaceholderByDecimals(limitDecimals, 1);
        }

        return inputParams.placeholder;
    }, [inputParams.placeholder, limitDecimals, type]);

    if (!hasInputParams) return null;

    const defaultPreComponent = (currencySymbol) => {
        return <div>{currencySymbol}</div>;
    };
    const defaultPostComponent = (currencySymbol) => {
        return <div>{currencySymbol}</div>;
    };

    let preComponent = inputParams?.preComponent || null;
    let postComponent = inputParams?.postComponent || null;

    if (params.colDef.crudType === 'currency' && params?.otherFields?.symbol) {
        const symbol = params.data?.[params.otherFields.symbol];
        if (symbol && params.currencyPosition) {
            if (params.currencyPosition === 'before') {
                preComponent = defaultPreComponent(symbol);
                postComponent = null;
            } else if (params.currencyPosition === 'after') {
                preComponent = null;
                postComponent = defaultPostComponent(symbol);
            }
        }
    }

    let classNames = ['w5-cell', classes.editableCell];

    if (params.align === 'right') classNames.push(classes.right);

    return (
        <div className={classNames.join(' ')}>
            <div>
                <Input
                    value={value}
                    onChange={handleOnChange}
                    onBlur={handleOnBlur}
                    onEnter={handleOnEnter}
                    onKeyDown={handleOnKeyDown}
                    onPaste={handleOnPaste}
                    {...inputParams}
                    placeholder={placeholder}
                    preComponent={preComponent}
                    postComponent={postComponent}
                    error={error}
                    overrides={overrides}
                    isFullWidth
                />
            </div>
        </div>
    );
});

export default InputCell;
